import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import useSpeechRecognition from "./useSpeechRecognition";
import useSoundEffects from "./useSoundEffects";

const ChatBotInput = forwardRef((
  {
    onSendMessage,
    onAttachFiles,
    attachedFiles,
    uploadedDocs,
    onRemoveFile,
    isLoading,
    disabled,
    placeholder,
    selectedLanguage = "english",
  },
  ref
) => {
  const defaultPlaceholders = {
    english: "Ask anything about BOQ, SSR rates, estimation...",
    marathi: "BOQ, SSR दर, अंदाज बद्दल काहीही विचारा..."
  };

  const finalPlaceholder = placeholder || defaultPlaceholders[selectedLanguage] || defaultPlaceholders.english;
  const [input, setInput] = useState("");
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    isListening,
    transcript,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const { playSend, playVoiceStart, playVoiceStop, playError } = useSoundEffects();

  useEffect(() => {
    if (transcript) {
      setInput((prev) => {
        const newText = prev ? prev + " " + transcript.trim() : transcript.trim();
        return newText;
      });
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
  }));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const content = input.trim();
    if (!content && attachedFiles.length === 0) return;
    if (isLoading) return;

    setInput("");
    onSendMessage(content, attachedFiles);
    playSend();
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleVoiceToggle = () => {
    if (!browserSupportsSpeechRecognition) {
      playError();
      alert("Speech recognition is not supported in this browser. Please try Google Chrome or Microsoft Edge.");
      return;
    }

    if (isListening) {
      stopListening();
      playVoiceStop();
    } else {
      startListening();
      playVoiceStart();
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAttachFiles(files);
    }
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onAttachFiles(files);
    }
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    }
  }, [input]);

  return (
    <div className="border-t border-slate-200/90 bg-white p-3 shadow-inner custom-scrollbar"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Uploaded Documents (Processed for RAG) */}
      {uploadedDocs && uploadedDocs.length > 0 && (
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5 animate-slide-in">
          <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider px-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Ready for Q&A:
          </span>
          {uploadedDocs.map((file, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-emerald-50/80 border border-emerald-200 text-slate-800 rounded-lg px-2.5 py-1 text-xs shadow-xs">
              <svg className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="truncate max-w-[120px] font-medium text-[11px] text-slate-700">{file.name}</span>
              <span className="text-[10px] text-slate-400 font-mono">({(file.size / 1024).toFixed(0)}KB)</span>
            </div>
          ))}
        </div>
      )}

      {/* Attached Files (Pending Upload) */}
      {attachedFiles.length > 0 && (
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5 animate-slide-in">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1">Attached:</span>
          {attachedFiles.map((file, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-orange-50/80 border border-orange-200 text-slate-800 rounded-lg px-2.5 py-1 text-xs shadow-xs">
              <svg className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <span className="truncate max-w-[120px] font-medium text-[11px] text-slate-700">{file.name}</span>
              <span className="text-[10px] text-slate-400 font-mono">({(file.size / 1024).toFixed(0)}KB)</span>
              <button
                onClick={() => onRemoveFile(i)}
                className="text-slate-400 hover:text-red-600 p-0.5 rounded transition ml-0.5"
                aria-label="Remove file"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <div className="flex items-end gap-2">
          {/* Main Textarea */}
          <div className="relative flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={finalPlaceholder}
              disabled={disabled || isLoading}
              className={`w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl text-sm leading-relaxed focus:outline-none focus:bg-white focus:ring-2 focus:ring-orange-400/40 focus:border-orange-500 resize-none transition-all ${
                isListening ? "ring-2 ring-emerald-500 bg-emerald-50/40" : ""
              } ${speechError ? "ring-2 ring-red-400 bg-red-50/40" : ""}`}
              rows={1}
              aria-label="Chat message input"
            />
          </div>

          {/* Action Toolbar */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Attachment Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                isLoading
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-500 hover:text-orange-600 hover:bg-orange-50 active:scale-95"
              }`}
              disabled={isLoading}
              title="Attach PDF, Word, Excel, or image"
              aria-label="Attach document"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
              className="hidden"
              id="chat-file-input"
            />

            {/* Voice Input Button */}
            {browserSupportsSpeechRecognition && (
              <button
                type="button"
                onClick={handleVoiceToggle}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                  isListening
                    ? "bg-emerald-500 text-white animate-pulse shadow-md shadow-emerald-500/30"
                    : "text-slate-500 hover:text-orange-600 hover:bg-orange-50 active:scale-95"
                }`}
                disabled={isLoading}
                title={isListening ? "Listening... (click to stop)" : "Voice input"}
                aria-label={isListening ? "Stop voice input" : "Start voice input"}
                aria-pressed={isListening}
              >
                {isListening ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>
            )}

            {/* Send Button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
              className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                isLoading || (!input.trim() && attachedFiles.length === 0)
                  ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md shadow-orange-500/25 active:scale-95 hover:scale-105"
              }`}
              aria-label="Send message"
              title="Send message"
            >
              {isLoading ? (
                <svg className="w-4 h-4 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {speechError && (
          <div className="mt-1 text-[11px] text-red-500 ml-2 animate-fade-in flex items-center gap-1">
            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{speechError}</span>
          </div>
        )}

        {isListening && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600 font-medium ml-2 animate-fade-in">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
            <span>Listening... speak now</span>
          </div>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400 px-1">
        <span>PDF, Word, Excel, Images supported</span>
        <span>Enter ↵ to send</span>
      </div>
    </div>
  );
});

ChatBotInput.displayName = "ChatBotInput";

export default ChatBotInput;